import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Plus, X, Trash2, AlertTriangle, ChevronRight, KeyRound } from "lucide-react";

export default function Users() {
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // user object
  const [resetUser, setResetUser] = useState(null); // user being reset
  const [newPassword, setNewPassword] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "employee", phone: "", department: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success("Employee added");
      setForm({ name: "", email: "", password: "", role: "employee", phone: "", department: "" });
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setBusy(true);
    try {
      await api.delete(`/users/${confirmDelete.id}`);
      toast.success(`Removed ${confirmDelete.name}`);
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  const doReset = async () => {
    if (!resetUser || !newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/users/${resetUser.id}/password`, { password: newPassword });
      toast.success(`Password reset for ${resetUser.name}`);
      setResetUser(null);
      setNewPassword("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen" data-testid="users-page">
      <header className="border-b border-zinc-200 page-px py-6 sm:py-8 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-uppercase mb-2">Workforce</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tighter" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>Employees</h1>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="new-user-button" className="bg-ikb text-white px-4 py-2.5 text-sm font-medium hover:bg-[#00227A] flex items-center gap-2">
          <Plus size={14} /> Add Employee
        </button>
      </header>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-zinc-300 w-full max-w-lg p-8" data-testid="user-form">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold tracking-tight">New Employee</h2>
              <button onClick={() => setShowForm(false)}><X size={18} /></button>
            </div>
            <form onSubmit={create} className="space-y-3">
              <Field label="Name *">
                <input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" required />
              </Field>
              <Field label="Email *">
                <input type="email" data-testid="user-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" required />
              </Field>
              <Field label="Password *">
                <input type="password" data-testid="user-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input" required minLength={6} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Role">
                  <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="input">
                    <option value="employee">Employee</option>
                    <option value="admin">Admin</option>
                  </select>
                </Field>
                <Field label="Department">
                  <input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="input" />
                </Field>
              </div>
              <Field label="Phone">
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input" />
              </Field>
              <button type="submit" disabled={busy} data-testid="user-submit-button" className="w-full bg-ikb text-white py-2.5 text-sm font-medium disabled:opacity-50">
                {busy ? "Saving…" : "Create"}
              </button>
            </form>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-zinc-300 w-full max-w-md" data-testid="confirm-delete-dialog">
            <div className="border-b border-zinc-200 px-6 py-4 flex items-center gap-2">
              <AlertTriangle size={16} className="text-red-600" />
              <h3 className="font-medium">Delete Employee</h3>
            </div>
            <div className="px-6 py-5 text-sm text-zinc-700">
              Are you sure you want to delete <span className="font-semibold">{confirmDelete.name}</span> ({confirmDelete.email})?
              This action cannot be undone.
            </div>
            <div className="border-t border-zinc-200 px-6 py-3 flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                data-testid="cancel-delete-button"
                className="px-4 py-2 text-xs uppercase tracking-wider border border-zinc-300 hover:bg-zinc-50"
              >Cancel</button>
              <button
                onClick={doDelete}
                disabled={busy}
                data-testid="confirm-delete-button"
                className="px-4 py-2 text-xs uppercase tracking-wider bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >{busy ? "Deleting…" : "Delete"}</button>
            </div>
          </div>
        </div>
      )}

      {resetUser && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-zinc-300 w-full max-w-md" data-testid="reset-password-dialog">
            <div className="border-b border-zinc-200 px-6 py-4 flex items-center gap-2">
              <KeyRound size={16} className="text-[#002FA7]" />
              <h3 className="font-medium">Reset Password</h3>
            </div>
            <div className="px-6 py-5 text-sm text-zinc-700 space-y-4">
              <p>Set a new password for <span className="font-semibold">{resetUser.name}</span> ({resetUser.email}).</p>
              <div>
                <label className="label-uppercase block mb-1.5">New Password</label>
                <input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  data-testid="reset-password-input"
                  placeholder="At least 6 characters"
                  className="w-full border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7] focus:ring-offset-1"
                  autoFocus
                />
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-1">
                  Share this password with the employee. They can change it later.
                </div>
              </div>
            </div>
            <div className="border-t border-zinc-200 px-6 py-3 flex justify-end gap-2">
              <button
                onClick={() => { setResetUser(null); setNewPassword(""); }}
                className="px-4 py-2 text-xs uppercase tracking-wider border border-zinc-300 hover:bg-zinc-50"
              >Cancel</button>
              <button
                onClick={doReset}
                disabled={busy || newPassword.length < 6}
                data-testid="confirm-reset-button"
                className="px-4 py-2 text-xs uppercase tracking-wider bg-ikb text-white hover:bg-[#00227A] disabled:opacity-50"
              >{busy ? "Saving…" : "Reset Password"}</button>
            </div>
          </div>
        </div>
      )}

      <div className="border border-zinc-200 mx-4 sm:mx-6 lg:mx-10 my-6 sm:my-8 overflow-x-auto">
        <div className="min-w-[700px]">
        <div className="grid grid-cols-12 label-uppercase border-b border-zinc-200 px-4 py-2 bg-zinc-50">
          <div className="col-span-3">Name</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-2">Department</div>
          <div className="col-span-2 text-right">Action</div>
        </div>
        {users.map((u) => (
          <div
            key={u.id}
            onClick={() => navigate(`/users/${u.id}`)}
            className="grid grid-cols-12 px-4 py-3 border-b border-zinc-200 text-sm hover:bg-zinc-50 items-center cursor-pointer"
            data-testid={`user-row-${u.id}`}
          >
            <div className="col-span-3 font-medium flex items-center gap-2">
              {u.name}
              {u.id === me?.id && <span className="text-[10px] uppercase tracking-wider text-zinc-400">(you)</span>}
            </div>
            <div className="col-span-3 text-zinc-600 truncate">{u.email}</div>
            <div className="col-span-2">
              <span className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${u.role === "admin" ? "bg-blue-50 border-blue-200 text-blue-800" : "bg-zinc-50 border-zinc-300 text-zinc-700"}`}>
                {u.role}
              </span>
            </div>
            <div className="col-span-2 text-zinc-600">{u.department || "—"}</div>
            <div className="col-span-2 text-right flex items-center justify-end gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); setResetUser(u); }}
                className="text-zinc-400 hover:text-[#002FA7]"
                data-testid={`reset-pw-${u.id}`}
                title="Reset password"
              >
                <KeyRound size={14} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setConfirmDelete(u); }}
                disabled={u.id === me?.id}
                className="text-zinc-400 hover:text-red-600 disabled:opacity-30 disabled:hover:text-zinc-400"
                data-testid={`delete-user-${u.id}`}
                title={u.id === me?.id ? "Cannot delete yourself" : "Delete"}
              >
                <Trash2 size={14} />
              </button>
              <ChevronRight size={14} className="text-zinc-300" />
            </div>
          </div>
        ))}
        </div>
      </div>

      <style>{`.input { width: 100%; border: 1px solid #d4d4d8; padding: 0.5rem 0.75rem; font-size: 0.875rem; outline: none; }
        .input:focus { border-color: #002FA7; box-shadow: 0 0 0 2px rgba(0,47,167,0.2); }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label-uppercase block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
